import { handleSystemErrorReport } from '@mb/lib/utils';
import { createDeepEqualSelector } from './index';
import { FilterData, SearchGlobalStateType } from '../../types';
import { getCharsNumber } from '../../utils/helpers';

export const filtersSelector = (state: SearchGlobalStateType) => state.filters;

export const currentFiltersSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) =>
    filters.currentFilters?.filter(
      (item: FilterData) => item.field !== 'residential_commercial',
    ) ?? [],
);

export const residentialCommercialFacetSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) =>
    filters.currentFilters?.find(
      (item: FilterData) => item.field === 'residential_commercial',
    ) ?? undefined,
);

export const primaryColorFilterSelector = createDeepEqualSelector(
  currentFiltersSelector,
  (filters) =>
    filters.find((filter: FilterData) => filter.field === 'virtual_color_group')
      ?.values || [],
);

export const currentPrimaryColorSelector = createDeepEqualSelector(
  primaryColorFilterSelector,
  (values = []) => values?.find((item: FilterData) => item.active),
);

export const allCurrentPrimaryColorsLabelsSelector = createDeepEqualSelector(
  primaryColorFilterSelector,
  (values = []) =>
    values
      ?.filter((item: FilterData) => item.active)
      ?.map((item: FilterData) => item.label),
);

export const colorShadesFilterSelector = createDeepEqualSelector(
  currentFiltersSelector,
  (filters) => {
    try {
      const shadesList =
        filters.find((filter: FilterData) => filter.field === 'virtual_color')
          ?.values || [];
      const finalList = shadesList?.filter(
        ({ count }: FilterData) => count !== 0,
      );
      return {
        allShades: shadesList,
        noZeroShades: finalList,
      };
    } catch (e) {
      handleSystemErrorReport(
        `Color shades filter error: ${e.message}`,
        'colorShadesFilterSelector',
      );
      return {
        allShades: [],
        noZeroShades: [],
      };
    }
  },
);

export const taxonomySelector = createDeepEqualSelector(
  currentFiltersSelector,
  (filters) =>
    filters.find((filter: FilterData) => filter.field === 'taxonomy')?.values ||
    [],
);

export const taxonomyParentsFilterSelector = createDeepEqualSelector(
  taxonomySelector,
  (filters) => {
    if (filters?.length > 0) {
      const parents = filters?.filter((item: FilterData) => {
        const arrowsNumber = getCharsNumber(item.label, '>');
        return (
          arrowsNumber === 1 &&
          ((item.count ?? 0) > 0 || ((item.count ?? 0) === 0 && item.active))
        );
      });
      return parents;
    }
    return [];
  },
);

export const taxonomyActiveParentsFilterSelector = createDeepEqualSelector(
  taxonomyParentsFilterSelector,
  (taxonomy) => taxonomy?.filter((filter: FilterData) => filter.active) || [],
);

export const taxonomyChildrenFilterSelector = createDeepEqualSelector(
  taxonomySelector,
  (filters) => {
    try {
      if (filters?.length > 0) {
        const selectedParent = filters?.find((item: FilterData) => {
          const arrowsNumber = getCharsNumber(item.label, '>');
          // Parent or sibling
          return arrowsNumber === 1 && item.active;
        });
        const selectedSibling = filters?.find((item: FilterData) => {
          const arrowsNumber = getCharsNumber(item.label, '>');
          // Parent or sibling
          return arrowsNumber === 2 && item.active;
        });
        const siblingLabel = selectedSibling?.label
          ?.split('>')
          ?.slice(0, -1)
          ?.join('>');
        const relativelabel = selectedParent?.label || siblingLabel;

        const onlyValues = filters?.filter((subItem: FilterData) => {
          const arrowsNumber = getCharsNumber(subItem.label, '>');
          const isCurrentGroupChild =
            relativelabel != null
              ? subItem?.label?.includes(relativelabel)
              : true;
          return (
            arrowsNumber === 2 &&
            isCurrentGroupChild &&
            ((subItem.count ?? 0) > 0 ||
              ((subItem.count ?? 0) === 0 && subItem.active))
          );
        });
        if (Array.isArray(onlyValues)) {
          return onlyValues;
        }
        return [];
      }
      return [];
    } catch (e) {
      console.error(e);
      handleSystemErrorReport(
        `Taxonomy children filter error: ${e.message}`,
        'taxonomyChildrenFilterSelector',
      );
      return [];
    }
  },
);

export const taxonomyActiveChildrenFilterSelector = createDeepEqualSelector(
  taxonomyChildrenFilterSelector,
  (taxonomy) => taxonomy?.filter((filter) => filter.active) || [],
);

export const appFiltersSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) => filters.appFilters || {},
);

export const appFiltersKeysSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) => Object.keys(filters.appFilters || {}),
);

export const appFiltersValuesSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) => Object.values(filters.appFilters || {}),
);

export const appFiltersNoColorsKeysSelector = createDeepEqualSelector(
  appFiltersKeysSelector,
  (filters = []) => filters?.filter((item) => !item.includes('virtual_color')),
);

export const searchToggleSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) => filters.searchToggle,
);

export const selectedMaterialsSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) => filters.selectedMaterials || [],
);

export const multiSelectToggleSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) => filters.multiSelectToggle,
);

export const selectAllToggleSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) => filters.selectAllToggle,
);

export const overrideParamsSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) => filters.overrideParams,
);

export const isFilterShownSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) => filters.isFilterShown,
);

export const isColorCircleTopbarShownSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) => filters.isColorCircleTopbarShown,
);

export const isRainbowClickedSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) => filters.isRainbowClicked,
);

export const mobileActiveFilterSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) => {
    if (Array.isArray(filters.mobileActiveFilters)) {
      return filters.mobileActiveFilters[
        filters.mobileActiveFilters.length - 1
      ];
    }
    return undefined;
  },
);

export const isBrandSwitchSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) => filters.isBrandSwitch,
);

export const isRepToolEnabledSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) => filters.isRepToolEnabled,
);

export const taxonomyFilterValueSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) => filters.taxonomyFilterValue,
);

export const reduxAdditionalFiltersSelector = createDeepEqualSelector(
  filtersSelector,
  (filters) => filters.additionalFilters,
);
